import { useEffect, useRef, useState } from "react";
import { appConfig, type Prize } from "../config/appConfig";
import { trackActivity } from "../services/activityEvents";
import { SafariFrameAnimation } from "./SafariFrameAnimation";

type RewardWheelProps = {
  onComplete: (prize: Prize) => void;
  onError: (message: string) => void;
  requestPrize: () => Promise<Prize>;
};

type WheelPhase = "ready" | "requesting" | "spinning" | "celebrating" | "complete";

const ASSET_ROOT = "/media/yx9935/wheel-v3";
const WIN_OVERLAY_WEBM_SRC = `${ASSET_ROOT}/dragon-coins-clean-v2/914go-ad-v1-yx9935-dragon-coins-clean-v2-vp9-alpha.webm`;
const GAME_FLOW_AUDIO_SRC = `${ASSET_ROOT}/audio/914go-ad-v1-yx9935-sfx-full-game-flow-v1-preview.mp3`;
const STOP_ANGLES: Record<Prize, number> = {
  "888": 0,
  "88": 300,
  "188": 240,
  "288": 180,
  "388": 120,
  "588": 60,
};
const FULL_TURNS = 5;

type WinOverlayFormat = "webm" | "frames";

function getWinOverlayFormat(): WinOverlayFormat | null {
  const { maxTouchPoints, platform, userAgent } = window.navigator;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent)
    || (platform === "MacIntel" && maxTouchPoints > 1);
  const isMacSafari = /Safari/.test(userAgent)
    && !/(Chrome|Chromium|CriOS|Edg|OPR|FxiOS)/.test(userAgent);
  const isDesktopChromium = /(Chrome|Chromium|Edg)\//.test(userAgent)
    && !/Android|Mobile/.test(userAgent)
    && !/(CriOS|EdgiOS|OPR|FxiOS)/.test(userAgent);

  if (isIOS || isMacSafari) return "frames";
  if (isDesktopChromium) return "webm";

  return null;
}

function resetMedia(media: HTMLMediaElement | null) {
  if (!media) return;

  media.pause();
  media.currentTime = 0;
}

export function RewardWheel({ onComplete, onError, requestPrize }: RewardWheelProps) {
  const [phase, setPhase] = useState<WheelPhase>("ready");
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState<Prize | null>(null);
  const [winOverlayFormat] = useState(getWinOverlayFormat);
  const [safariOverlayKey, setSafariOverlayKey] = useState(0);
  const phaseRef = useRef<WheelPhase>("ready");
  const lockedPrizeRef = useRef<Prize | null>(null);
  const winVideoRef = useRef<HTMLVideoElement>(null);
  const gameFlowAudioRef = useRef<HTMLAudioElement>(null);

  const completeWithLockedPrize = () => {
    if (!lockedPrizeRef.current) return;

    const selectedPrize = lockedPrizeRef.current;

    phaseRef.current = "complete";
    setResult(selectedPrize);
    setPhase("complete");
    trackActivity("wheel_completed", {
      prize: selectedPrize,
      demo: appConfig.demoMode,
    });
    onComplete(selectedPrize);
  };

  const finishCelebration = () => {
    if (phaseRef.current !== "celebrating") return;

    resetMedia(winVideoRef.current);
    completeWithLockedPrize();
  };

  const finishSpin = () => {
    if (phaseRef.current !== "spinning" || !lockedPrizeRef.current) return;

    if (!winOverlayFormat) {
      completeWithLockedPrize();
      return;
    }

    phaseRef.current = "celebrating";
    setPhase("celebrating");

    if (winOverlayFormat === "frames") {
      setSafariOverlayKey((current) => current + 1);
      return;
    }

    const video = winVideoRef.current;

    if (!video) {
      console.error("透明中獎動畫尚未掛載", {
        src: WIN_OVERLAY_WEBM_SRC,
        userAgent: window.navigator.userAgent,
      });
      finishCelebration();
      return;
    }

    resetMedia(video);

    try {
      const videoPlayback = video.play();

      void videoPlayback.catch((error: unknown) => {
        console.error("透明中獎動畫播放失敗", {
          error,
          src: video.currentSrc || video.src || WIN_OVERLAY_WEBM_SRC,
          userAgent: window.navigator.userAgent,
        });
        finishCelebration();
      });
    } catch (error) {
      console.error("透明中獎動畫啟動失敗", {
        error,
        src: video.currentSrc || video.src || WIN_OVERLAY_WEBM_SRC,
        userAgent: window.navigator.userAgent,
      });
      finishCelebration();
    }
  };

  useEffect(() => () => {
    resetMedia(winVideoRef.current);
    resetMedia(gameFlowAudioRef.current);
  }, []);

  const spin = async () => {
    if (phaseRef.current !== "ready") return;

    phaseRef.current = "requesting";
    setPhase("requesting");
    lockedPrizeRef.current = null;
    trackActivity("wheel_started");

    const audio = gameFlowAudioRef.current;

    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 1;
      audio.loop = false;
      void audio.play().catch((error: unknown) => {
        console.error("完整遊戲流程音效播放失敗", {
          error,
          src: audio.currentSrc || audio.src || GAME_FLOW_AUDIO_SRC,
          userAgent: window.navigator.userAgent,
        });
        resetMedia(audio);
      });
    }

    try {
      const selectedPrize = await requestPrize();

      if (phaseRef.current !== "requesting") return;

      lockedPrizeRef.current = selectedPrize;
      phaseRef.current = "spinning";
      setPhase("spinning");
      setRotation(FULL_TURNS * 360 + STOP_ANGLES[selectedPrize]);
    } catch (error) {
      if (phaseRef.current !== "requesting") return;

      resetMedia(audio);
      phaseRef.current = "ready";
      setPhase("ready");
      onError(error instanceof Error ? error.message : "抽獎失敗，請重新嘗試");
    }
  };

  const isLocked = phase !== "ready";

  return (
    <>
      <div className="wheel-stage">
        <picture className="wheel-scene-layer" aria-hidden="true">
          <source
            srcSet={`${ASSET_ROOT}/914go-ad-v1-yx9935-wheel-scene-base-1080x1920.webp`}
            type="image/webp"
          />
          <img
            src={`${ASSET_ROOT}/914go-ad-v1-yx9935-wheel-scene-base-1080x1920.png`}
            alt=""
          />
        </picture>

        <picture className="wheel-logo-enhancement-layer" aria-hidden="true">
          <source
            srcSet={`${ASSET_ROOT}/logo-enhancement/914go-ad-v1-yx9935-wheel-logo-enhancement-overlay-v1.webp`}
            type="image/webp"
          />
          <img
            src={`${ASSET_ROOT}/logo-enhancement/914go-ad-v1-yx9935-wheel-logo-enhancement-overlay-v1-1080x1920.png`}
            alt=""
          />
        </picture>

        <picture
          className="wheel-disc-layer"
          style={{ transform: `rotate(${rotation}deg)` }}
          aria-hidden="true"
          onTransitionEnd={(event) => {
            if (
              event.target === event.currentTarget
              && event.propertyName === "transform"
            ) {
              finishSpin();
            }
          }}
        >
          <source
            srcSet={`${ASSET_ROOT}/914go-ad-v1-yx9935-wheel-disc-560.webp`}
            type="image/webp"
          />
          <img
            src={`${ASSET_ROOT}/914go-ad-v1-yx9935-wheel-disc-560.png`}
            alt=""
          />
        </picture>

        <button
          aria-label={phase === "ready" ? "啟動輪盤" : "輪盤已鎖定"}
          className="wheel-start-button"
          type="button"
          onClick={spin}
          disabled={isLocked}
        >
          <img
            src={`${ASSET_ROOT}/914go-ad-v1-yx9935-wheel-start-button-fixed-original.png`}
            alt=""
          />
        </button>

        {winOverlayFormat === "webm" && (
          <div
            className={`wheel-dragon-overlay${phase === "celebrating" ? " is-visible" : ""}`}
            aria-hidden={phase !== "celebrating"}
          >
            <video
              ref={winVideoRef}
              className="wheel-dragon-overlay-video"
              src={WIN_OVERLAY_WEBM_SRC}
              muted
              playsInline
              preload="auto"
              onEnded={finishCelebration}
              onError={() => {
                if (phaseRef.current !== "celebrating") return;

                const video = winVideoRef.current;
                console.error("透明中獎動畫載入失敗", {
                  error: video?.error,
                  src: video?.currentSrc || video?.src || WIN_OVERLAY_WEBM_SRC,
                  userAgent: window.navigator.userAgent,
                });
                finishCelebration();
              }}
            />
          </div>
        )}
        {winOverlayFormat === "frames" && (
          <div
            className={`wheel-dragon-overlay${phase === "celebrating" ? " is-visible" : ""}`}
            aria-hidden={phase !== "celebrating"}
          >
            <SafariFrameAnimation
              playing={phase === "celebrating"}
              playbackKey={safariOverlayKey}
              onEnded={finishCelebration}
              onError={() => {
                if (phaseRef.current !== "celebrating") return;

                console.error("Safari Canvas 中獎動畫播放失敗", {
                  userAgent: window.navigator.userAgent,
                });
                finishCelebration();
              }}
            />
          </div>
        )}
        <audio
          ref={gameFlowAudioRef}
          className="wheel-win-audio"
          src={GAME_FLOW_AUDIO_SRC}
          preload="auto"
        />
      </div>

      <span className="sr-only" role="status">
        {phase === "spinning" && "輪盤轉動中"}
        {phase === "celebrating" && "輪盤已停止，正在播放中獎動畫"}
        {phase === "complete" && `本次結果為 ${result} 點`}
      </span>

      {phase === "complete" && result && (
        <div className="result-card">
          <small>本次示意結果</small>
          <strong>{result} 點</strong>
        </div>
      )}
    </>
  );
}
